import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import ScrollToTop from "@/site/ScrollToTop";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import ClientsList from "@/pages/ClientsList";
import ClientForm from "@/pages/ClientForm";
import ClientDetail from "@/pages/ClientDetail";
import VisitsList from "@/pages/VisitsList";
import VisitForm from "@/pages/VisitForm";
import PublicPricing from "@/pages/PublicPricing";
import Relances from "@/pages/Relances";
import AdminDashboard from "@/pages/AdminDashboard";

import SiteLayout from "@/site/SiteLayout";
import Home from "@/site/Home";
import Matieres from "@/site/Matieres";
import CommentCaMarche from "@/site/CommentCaMarche";
import APropos from "@/site/APropos";
import Contact from "@/site/Contact";
import CityPage from "@/site/CityPage";
import BennePage from "@/site/BennePage";
import GravatsPage from "@/site/GravatsPage";
import TarifsVillePage from "@/site/TarifsVillePage";
import { Calculateur, Glossaire, FaqPros, Comparatif } from "@/site/Resources";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50">
        <div className="text-stone-400 font-bold">Chargement...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/connexion" replace />;
  return <AppShell>{children}</AppShell>;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50">
        <div className="text-stone-400 font-bold">Chargement...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/connexion" replace />;
  if (user.role !== "admin") return <Navigate to="/mon-espace" replace />;
  return <AppShell>{children}</AppShell>;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-stone-50">
        <div className="text-stone-400 font-bold">Chargement...</div>
      </div>
    );
  }
  if (user) {
    const target = user.role === "admin" ? "/espace/admin" : "/mon-espace";
    return <Navigate to={target} replace />;
  }
  return children;
}

function Site({ children }) {
  return <SiteLayout>{children}</SiteLayout>;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
          <Routes>
            {/* Public marketing site */}
            <Route path="/" element={<Site><Home /></Site>} />
            <Route path="/dechets" element={<Site><Matieres /></Site>} />
            <Route path="/comment-ca-marche" element={<Site><CommentCaMarche /></Site>} />
            <Route path="/a-propos" element={<Site><APropos /></Site>} />
            <Route path="/contact" element={<Site><Contact /></Site>} />
            <Route path="/recyclage/:slug" element={<Site><CityPage /></Site>} />
            <Route path="/benne/:slug" element={<Site><BennePage /></Site>} />
            <Route path="/gravats/:slug" element={<Site><GravatsPage /></Site>} />
            <Route path="/tarifs/:slug" element={<Site><TarifsVillePage /></Site>} />
            <Route path="/calculateur-volume" element={<Site><Calculateur /></Site>} />
            <Route path="/glossaire" element={<Site><Glossaire /></Site>} />
            <Route path="/faq-dechets-pros" element={<Site><FaqPros /></Site>} />
            <Route path="/comparatif" element={<Site><Comparatif /></Site>} />

            {/* Public pricing share */}
            <Route path="/tarifs/:token" element={<PublicPricing />} />

            {/* Auth (public) */}
            <Route path="/connexion" element={<PublicOnly><AuthPage mode="login" /></PublicOnly>} />
            <Route path="/login" element={<Navigate to="/connexion" replace />} />
            <Route path="/register" element={<PublicOnly><AuthPage mode="register" /></PublicOnly>} />

            {/* Commercial CRM (protected) */}
            <Route path="/mon-espace" element={<Protected><Dashboard /></Protected>} />
            <Route path="/clients" element={<Protected><ClientsList /></Protected>} />
            <Route path="/clients/new" element={<Protected><ClientForm /></Protected>} />
            <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
            <Route path="/clients/:id/edit" element={<Protected><ClientForm /></Protected>} />
            <Route path="/visites" element={<Protected><VisitsList /></Protected>} />
            <Route path="/visites/nouvelle" element={<Protected><VisitForm /></Protected>} />
            <Route path="/relances" element={<Protected><Relances /></Protected>} />

            {/* Admin */}
            <Route path="/espace/admin" element={<AdminOnly><AdminDashboard /></AdminOnly>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
